import { Alert, Box, Typography } from "@mui/material";
import { useMemo, useCallback, useState } from "react";
import { TimeRangeFilterBar } from "../components/filters/TimeRangeFilterBar";
import { RefreshControls } from "../components/filters/RefreshControls";
import { FilterBar } from "../components/filters/FilterBar";
import { Panel } from "../components/layout/Panel";
import type { TraceSummary } from "../api/client";
import { useTimeRangeFromQuery } from "../hooks/useRouteParams";
import { CompGrid, type Column, type ColumnFilterProps } from "../components/grid/CompGrid";
import { createTraceIdColumn } from "../components/grid/columns/traceColumns";
import { useMetadata } from "../hooks/useMetadata";
import { useTraces } from "../hooks/useTraces";
import { useGridFilters } from "../hooks/useGridFilters";
import { PageErrorState } from "../components/pages/PageErrorState";
import { PageLoadingState } from "../components/pages/PageLoadingState";
import { useHierarchyContext } from "../contexts/HierarchyContext";
import {
  GridIdsFilterPopover,
  GridDurationFilterPopover,
  GridCheckboxSearchFilterPopover,
} from "../components/filters/grid";
import { GridSpanCountFilterPopover } from "../components/filters/grid/GridSpanCountFilterPopover";

function computeTraceMeta(traceSummary: TraceSummary) {
  const start = new Date(traceSummary.start_time);
  const end = new Date(traceSummary.end_time);
  const durationMs = end.getTime() - start.getTime();
  const spanCount = traceSummary.span_count;

  return { traceSummary, start, end, durationMs, spanCount };
}

export function PageTracesQuery() {
  const { hierarchy } = useHierarchyContext();
  const serviceSlug = hierarchy?.service?.slug ?? null;
  const { range } = useTimeRangeFromQuery();
  const { filters, setFilters } = useGridFilters(serviceSlug, "traces");

  const [refreshToken, setRefreshToken] = useState(0);

  const { metadata } = useMetadata(serviceSlug);
  const metadataFormatted = useMemo(() => {
    if (!metadata) return { services: [], instances: [] };
    return {
      services: metadata.services.map((s) => s.service_name),
      instances: metadata.services.flatMap((s) => s.instances.map((i) => i.instance_id)),
    };
  }, [metadata]);

  const { traces, loading, error, isFetching } = useTraces({
    serviceSlug,
    timeRange: range,
    serviceNames: filters.serviceNames,
    serviceInstances: filters.serviceInstances,
    traceIds: filters.traceIds,
    minDurationMs: filters.duration.min,
    maxDurationMs: filters.duration.max,
    minSpanCount: filters.spanCount.min,
    maxSpanCount: filters.spanCount.max,
    sort: filters.sort,
    order: filters.order,
    enabled: !!serviceSlug,
    limit: 500,
    refreshToken,
  });

  const handleSortChange = useCallback(
    (columnId: string, direction: "asc" | "desc") => {
      const sortMap: Record<string, "start_time" | "end_time" | "duration" | "span_count"> = {
        trace_id: "start_time",
        duration: "duration",
        start_time: "start_time",
        end_time: "end_time",
        span_count: "span_count",
      };
      const sort = sortMap[columnId] ?? "start_time";
      setFilters((prev) => ({ ...prev, sort, order: direction }));
    },
    [setFilters],
  );

  const columnFilterProps = useMemo((): Record<string, ColumnFilterProps> => {
    return {
      trace_id: {
        value: filters.traceIds,
        onChange: (v) => setFilters((prev) => ({ ...prev, traceIds: (v as string[]) ?? [] })),
        hasActive: filters.traceIds.length > 0,
      },
      duration: {
        value: filters.duration,
        onChange: (v) => setFilters((prev) => ({ ...prev, duration: v as typeof prev.duration })),
        hasActive:
          filters.duration.min != null ||
          (filters.duration.max != null && filters.duration.max > 0),
      },
      span_count: {
        value: filters.spanCount,
        onChange: (v) => setFilters((prev) => ({ ...prev, spanCount: v as typeof prev.spanCount })),
        hasActive:
          filters.spanCount.min != null ||
          (filters.spanCount.max != null && filters.spanCount.max > 0),
      },
      service_name: {
        value: filters.serviceNames,
        onChange: (v) => setFilters((prev) => ({ ...prev, serviceNames: (v as string[]) ?? [] })),
        hasActive: filters.serviceNames.length > 0,
      },
      service_instance: {
        value: filters.serviceInstances,
        onChange: (v) =>
          setFilters((prev) => ({ ...prev, serviceInstances: (v as string[]) ?? [] })),
        hasActive: filters.serviceInstances.length > 0,
      },
    };
  }, [filters, setFilters]);

  const columns: Column<ReturnType<typeof computeTraceMeta>>[] = useMemo(
    () => [
      {
        ...createTraceIdColumn<ReturnType<typeof computeTraceMeta>>({
          serviceSlug: serviceSlug ?? "",
          getTraceId: (item) => item.traceSummary.trace_id,
          truncateAt: 16,
        }),
        sortKey: "start_time",
        filterable: true,
        filterRender: (props) => (
          <GridIdsFilterPopover {...props} label="Trace IDs" placeholder="Add trace ID" />
        ),
      },
      {
        id: "duration",
        label: "Duration",
        sortable: true,
        sortKey: "duration",
        sortValue: (item) => item.durationMs,
        render: (item) => (
          <Typography variant="body2">{(item.durationMs / 1000).toFixed(3)}s</Typography>
        ),
        filterable: true,
        filterRender: (props) => <GridDurationFilterPopover {...props} />,
      },
      {
        id: "start_time",
        label: "Start Time",
        sortable: true,
        sortKey: "start_time",
        sortValue: (item) => item.start.getTime(),
        render: (item) => <Typography variant="body2">{item.start.toLocaleString()}</Typography>,
      },
      {
        id: "end_time",
        label: "End Time",
        sortable: true,
        sortKey: "end_time",
        sortValue: (item) => item.end.getTime(),
        render: (item) => <Typography variant="body2">{item.end.toLocaleString()}</Typography>,
      },
      {
        id: "span_count",
        label: "Spans",
        sortable: true,
        sortKey: "span_count",
        sortValue: (item) => item.traceSummary.span_count,
        render: (item) => <Typography variant="body2">{item.traceSummary.span_count}</Typography>,
        filterable: true,
        filterRender: (props) => <GridSpanCountFilterPopover {...props} />,
      },
      {
        id: "service_name",
        label: "Service",
        sortable: true,
        sortValue: (item) => item.traceSummary.service_name,
        render: (item) => <Typography variant="body2">{item.traceSummary.service_name}</Typography>,
        hiddenByDefault: true,
        filterable: true,
        filterRender: (props) => (
          <GridCheckboxSearchFilterPopover
            {...props}
            label="Service Names"
            options={metadataFormatted.services}
          />
        ),
      },
      {
        id: "service_instance",
        label: "Instance",
        sortable: true,
        sortValue: (item) => item.traceSummary.service_instance ?? "",
        render: (item) => (
          <Typography variant="body2">{item.traceSummary.service_instance ?? "—"}</Typography>
        ),
        hiddenByDefault: true,
        filterable: true,
        filterRender: (props) => (
          <GridCheckboxSearchFilterPopover
            {...props}
            label="Service Instances"
            options={metadataFormatted.instances}
          />
        ),
      },
    ],
    [serviceSlug, metadataFormatted.services, metadataFormatted.instances],
  );

  const sortColumnMap: Record<string, string> = {
    start_time: "start_time",
    end_time: "end_time",
    duration: "duration",
    span_count: "span_count",
  };
  const sortColumnId =
    Object.entries(sortColumnMap).find(([, v]) => v === filters.sort)?.[0] ?? "start_time";

  if (!serviceSlug) {
    return (
      <Box>
        <Alert severity="warning">
          No Service in URL. Please navigate from a product’s services list.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}>
      <Panel>
        <FilterBar>
          <TimeRangeFilterBar />
          <Box data-filterbar-wide="true" sx={{ maxWidth: "none", minWidth: "auto" }}>
            <RefreshControls setRefreshToken={setRefreshToken} isFetching={isFetching} />
          </Box>
        </FilterBar>
        {error ? <PageErrorState message={error} sx={{ mt: 1.5 }} /> : null}
        {loading ? <PageLoadingState message="Loading traces..." sx={{ mt: 1.5 }} /> : null}
      </Panel>

      <CompGrid
        items={traces.map(computeTraceMeta)}
        loading={loading}
        error={error}
        emptyMessage="No traces found for the selected filters"
        keyExtractor={(item) => item.traceSummary.trace_id}
        columns={columns}
        visibleColumns={filters.visibleColumns}
        onVisibleColumnsChange={(ids) => setFilters((prev) => ({ ...prev, visibleColumns: ids }))}
        sortColumn={sortColumnId}
        sortDirection={filters.order}
        onSortChange={handleSortChange}
        serverSort
        columnFilterProps={columnFilterProps}
      />
    </Box>
  );
}
